"use client";
import type { RowData } from "@tanstack/react-table";

import { useEffect, useMemo } from "react";
import type { RecordDataSource } from "../types";

export interface CursorWindow<TData extends RowData> {
  rows: TData[];
  hasNext: boolean;
  page: number;
}

export function useCursorWindow<TData extends RowData>(
  data: RecordDataSource<TData>,
  page: number,
  pageSize: number,
  onPageChange?: (page: number) => void,
): CursorWindow<TData> {
  const isCursor = data.mode === "cursor";
  const rows = data.rows;
  const loadMore = isCursor ? data.loadMore : undefined;
  const canLoadMore = isCursor ? data.canLoadMore : false;

  const completed = isCursor && !data.canLoadMore && !data.isLoading && !data.isLoadingMore;
  const effectivePage = completed ? Math.min(page, Math.max(1, Math.ceil(rows.length / pageSize))) : page;
  const needed = effectivePage * pageSize;
  useEffect(() => {
    if (effectivePage !== page) onPageChange?.(effectivePage);
  }, [effectivePage, page, onPageChange]);
  const shortfall = needed - rows.length;

  useEffect(() => {
    if (!loadMore || !canLoadMore || shortfall <= 0) return;
    loadMore(shortfall);
  }, [loadMore, canLoadMore, shortfall]);

  return useMemo(() => {
    if (!isCursor) return { rows, hasNext: false, page };
    return {
      rows: rows.slice((effectivePage - 1) * pageSize, needed),
      page: effectivePage,
      hasNext: rows.length > needed || canLoadMore,
    };
  }, [isCursor, rows, page, effectivePage, pageSize, needed, canLoadMore]);
}
